import React, { useContext } from 'react';
import { UserContext } from './components/context/UserContext';
import { UserProvider } from './components/context/UserContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import PersonalList from './components/personal/list/PagePersonalList';
import Login from './components/login/LoginPage';
import PersonalEdit from './components/personal/edit/PagePersonalEdit';
import ShrinkOnScrollAppBar from './components/common/ShrinkOnScrollAppBar';
import NavBar from './components/common/NavBar';
import { Container } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { StationProvider } from './components/context/StationContext';
import { CalendarProvider } from './components/context/CalendarContext';
import CalendarPage from './components/calendar/CalendarPage';
import ConfigurationPage from './components/configuration/ConfigurationPage';
import StationPage from './components/configuration/StationConfigPage';
import PositionsConfigPage from './components/configuration/PositionsConfigPage';
import UserConfigPage from './components/configuration/UserConfigPage';
import TypeLicensePage from './components/configuration/TypeLicensePage';
import WorkshiftConfigPage from './components/configuration/WorkshiftConfigPage';
import StaffingPage from './components/configuration/staffing/StaffingPage';
import PublicHolidayConfigPage from './components/configuration/PublicHolidayConfigPage';
import GeneralConfigPage from './components/configuration/GeneralConfigPage';
import RolePermissionsAdminPage from './components/configuration/RolePermissionsAdminPage';
import ReportsPage from './components/reports/ReportsPage';
import AuditPage from './components/audit/AuditPage';
import HelpPage from './components/help/HelpPage';
import {
  CALENDAR_PAGE_PERMISSIONS,
  CONFIGURATION_PERMISSIONS,
  GENERAL_CONFIG_PAGE_PERMISSIONS,
  hasAllowedRole,
  hasAnyRequiredPermission,
  LICENSE_CONFIG_PAGE_PERMISSIONS,
  PERSONAL_EDIT_PERMISSIONS,
  PERSONAL_PAGE_PERMISSIONS,
  POSITION_PAGE_PERMISSIONS,
  REPORT_PERMISSIONS,
  STATION_PAGE_PERMISSIONS,
  USER_CONFIG_PAGE_PERMISSIONS,
  WORKSHIFT_CONFIG_PAGE_PERMISSIONS,
  STAFFING_PAGE_PERMISSIONS,
} from './config/routePermissions';

import './App.css';

const ForbiddenPage = () => (
  <Box sx={{ p: 4 }}>
    <Typography variant="h5" gutterBottom>No tienes permiso para acceder a esta sección.</Typography>
    <Typography color="text.secondary">
      Si entendés que deberías verla, revisemos el rol y los permisos asignados.
    </Typography>
  </Box>
);

const ProtectedRoute = ({ children, anyPermissions = [], allowedRoles = [] }) => {
  const { sessionReady, isAuthenticated, permissions, user } = useContext(UserContext);

  if (!sessionReady) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAllowedRole(user.role, allowedRoles)) {
    return <ForbiddenPage />;
  }

  if (!hasAnyRequiredPermission(permissions, anyPermissions)) {
    return <ForbiddenPage />;
  }

  return children;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionReady, isAuthenticated, authErrorCode } = useContext(UserContext);

  React.useEffect(() => {
    const isDev = process.env.REACT_APP_IS_DEV === 'true';
    let page = '';
    switch (location.pathname) {
      case '/planificador':
        page = 'Planificador';
        break;
      case '/personal_table':
        page = 'Personal';
        break;
      case '/configuracion':
        page = 'Configuración';
        break;
      case '/station':
        page = 'Estaciones';
        break;
      case '/reportes':
        page = 'Reportes';
        break;
      case '/auditoria':
        page = 'Auditoría';
        break;
      case '/permisos':
        page = 'Gestión de permisos';
        break;
      case '/ayuda':
        page = 'Ayuda';
        break;
      case '/usuario':
        page = 'Usuario';
        break;
      case '/personal_edit':
        page = 'Editar Personal';
        break;
      case '/login':
        page = 'Login';
        break;
      default:
        page = '';
    }
    const baseTitle = isDev ? 'DEV - Time Planner' : 'Time Planner';
    document.title = page ? `${baseTitle} - ${page}` : baseTitle;
  }, [location.pathname]);

  React.useEffect(() => {
    if (!sessionReady) {
      return;
    }

    if (isAuthenticated && location.pathname === '/login') {
      navigate('/personal_table', { replace: true });
      return;
    }

    if (!isAuthenticated && location.pathname !== '/login') {
      const nextUrl = authErrorCode
        ? `/login?auth=${encodeURIComponent(authErrorCode)}`
        : '/login';
      navigate(nextUrl, { replace: true });
    }
  }, [authErrorCode, isAuthenticated, location.pathname, navigate, sessionReady]);

  if (!sessionReady && location.pathname !== '/login') {
    return null;
  }

  return (
    <div className="App">
      {location.pathname !== '/login' && (
        <>
          <ShrinkOnScrollAppBar>
            <NavBar />
          </ShrinkOnScrollAppBar>
          <Box sx={{ height: 64, minHeight: '64px' }} />
        </>
      )}
      <Container maxWidth={false} disableGutters>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute anyPermissions={PERSONAL_PAGE_PERMISSIONS}><PersonalList /></ProtectedRoute>} />
          <Route path="/personal_table" element={<ProtectedRoute anyPermissions={PERSONAL_PAGE_PERMISSIONS}><PersonalList /></ProtectedRoute>} />
          <Route path="/personal_edit" element={<ProtectedRoute anyPermissions={PERSONAL_EDIT_PERMISSIONS}><PersonalEdit /></ProtectedRoute>} />
          <Route path="/planificador" element={<ProtectedRoute anyPermissions={CALENDAR_PAGE_PERMISSIONS}><CalendarPage pageName="Planificador" /></ProtectedRoute>} />
          <Route path="/configuracion" element={<ProtectedRoute anyPermissions={CONFIGURATION_PERMISSIONS}><ConfigurationPage pageName="Configuración" /></ProtectedRoute>} />
          <Route path="/station" element={<ProtectedRoute anyPermissions={STATION_PAGE_PERMISSIONS}><StationPage /></ProtectedRoute>} />
          <Route path="/positions" element={<ProtectedRoute anyPermissions={POSITION_PAGE_PERMISSIONS}><PositionsConfigPage /></ProtectedRoute>} />
          <Route path="/reportes" element={<ProtectedRoute anyPermissions={REPORT_PERMISSIONS}><ReportsPage pageName="Reportes" /></ProtectedRoute>} />
          <Route path="/auditoria" element={<ProtectedRoute allowedRoles={['super_admin']}><AuditPage /></ProtectedRoute>} />
          <Route path="/permisos" element={<ProtectedRoute allowedRoles={['super_admin']}><RolePermissionsAdminPage /></ProtectedRoute>} />
          <Route path="/ayuda" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
          <Route path="/user-config" element={<ProtectedRoute anyPermissions={USER_CONFIG_PAGE_PERMISSIONS}><UserConfigPage /></ProtectedRoute>} />
          <Route path="/type-license" element={<ProtectedRoute anyPermissions={LICENSE_CONFIG_PAGE_PERMISSIONS}><TypeLicensePage /></ProtectedRoute>} />
          <Route path="/public-holidays-config" element={<ProtectedRoute anyPermissions={LICENSE_CONFIG_PAGE_PERMISSIONS}><PublicHolidayConfigPage /></ProtectedRoute>} />
          <Route path="/general-config" element={<ProtectedRoute anyPermissions={GENERAL_CONFIG_PAGE_PERMISSIONS}><GeneralConfigPage /></ProtectedRoute>} />
          <Route path="/workshift-config" element={<ProtectedRoute anyPermissions={WORKSHIFT_CONFIG_PAGE_PERMISSIONS}><WorkshiftConfigPage /></ProtectedRoute>} />
          <Route path="/dotacion" element={<ProtectedRoute anyPermissions={STAFFING_PAGE_PERMISSIONS}><StaffingPage /></ProtectedRoute>} />
        </Routes>
      </Container>
    </div>
  );
}

function App() {
  return (
    <SnackbarProvider maxSnack={3}>
      <UserProvider>
        <StationProvider>
          <CalendarProvider>
            <Router>
              <AppContent />
            </Router>
          </CalendarProvider>
        </StationProvider>
      </UserProvider>
    </SnackbarProvider>
  );
}

export default App;
